import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Gallery() {
  const { headings, gallery_items } = useSiteContent();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (gallery_items) { setItems(gallery_items); return; }
    fetch(`${API}/gallery`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setItems)
      .catch(() => setItems([]));
  }, [gallery_items]);

  if (items.length === 0) return null;

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32 bg-navy border-y border-slate-800/60 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-16 flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="text-electric font-semibold tracking-[0.3em] text-sm uppercase mb-4" data-testid="gallery-eyebrow">{headings.gallery.eyebrow}</p>
            <h2 className="font-bebas text-4xl md:text-6xl tracking-tight chrome-text">
              {headings.gallery.title}
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl font-light">
              {headings.gallery.description}
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-testid="gallery-grid">
          {items.map((item, i) => (
            <motion.figure
              key={item.id}
              data-testid={`gallery-item-${item.id}`}
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              className={`group relative overflow-hidden rounded-2xl border border-slate-800 bg-navy-light ${
                i % 5 === 0 ? "row-span-2 aspect-[3/4]" : "aspect-square"
              }`}
            >
              <img
                src={item.image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${item.image}` : item.image}
                alt={item.title}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = "/assets/emblem.webp";
                }}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <figcaption className="absolute inset-0 bg-gradient-to-t from-navy-deep/95 via-navy-deep/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-5">
                <span className="text-electric text-xs font-semibold tracking-[0.25em] uppercase">{item.category}</span>
                <span className="mt-1 flex items-center gap-2 text-white font-bebas text-2xl tracking-wide">
                  {item.title}
                  <ArrowUpRight className="w-4 h-4 text-electric" />
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
